// MediaCapture/MediaRecorder.jsx
//
// Public entry for the reusable in-browser camera/mic recorder. The heavy body
// (MediaRecorderInner + the getUserMedia/MediaRecorder capture logic) is
// code-split behind React.lazy, so it is fetched only when a recorder is
// actually mounted — keeping it out of the main bundle (audit: 4.7MB bundle).
//
// This is additive. By default it is flag-gated: when REACT_APP_MEDIA_RECORDER
// is OFF (default) this renders nothing and loads nothing, so the SegmentBuilder
// Video/Audio blocks are byte-identical to today's upload-only behaviour. When
// ON, those block editors render it as a "Record" option alongside "Upload"; the
// SAME component also serves the narrator-Portrait + share-video narration
// (configure via the `mode`, `maxDurationMs`, and `onComplete` props).
//
// A caller may pass `enabled` to OVERRIDE the global flag for its own scoped,
// already-gated surface. The Capture composer does this: the whole Capture
// surface is gated by CAPTURE_ENABLED, so its Video/Audio tabs mount the
// recorder ACTIVE (record + upload both offered — full mobile parity) without
// flipping the global REACT_APP_MEDIA_RECORDER flag, which would also un-dark
// the still-rolling-out SegmentBuilder recorder. When `enabled` is omitted the
// behaviour is unchanged (falls back to the global flag).
//
// Props:
//   mode          "video" (camera + mic) | "audio" (mic only)   default "video"
//   maxDurationMs hard cap on a single recording                default 5 min
//   enabled       explicit on/off override; when undefined, uses the global flag
//   onComplete    (media) => void — receives the SAME { id, src, title,
//                 loading, type } shape the Upload path produces, so the blocks
//                 consume a recording identically to an uploaded file.
//   autoStage     ONE-CONFIRM mode (QA-86): stopping hands the clip straight to
//                 `onUse`, with no second "Use this recording" button. Opt-in, so
//                 SegmentBuilder's two-step flow is untouched.

import React, { Suspense } from "react";
import { MEDIA_RECORDER_ENABLED } from "../../constants/featureFlags";
import { DEFAULT_MAX_DURATION_MS } from "./useMediaRecorder";

const MediaRecorderInner = React.lazy(() => import("./MediaRecorderInner"));

export default function MediaRecorder({
  mode = "video",
  maxDurationMs = DEFAULT_MAX_DURATION_MS,
  enabled,
  onComplete,
  onUse,
  autoStage = false,
}) {
  // Gate: an explicit `enabled` prop wins; otherwise fall back to the global
  // flag. When the resolved value is falsy nothing renders and the inner chunk
  // is never requested, so a drop-in mount stays safe.
  const active = enabled === undefined ? MEDIA_RECORDER_ENABLED : enabled;
  if (!active) return null;

  return (
    <Suspense
      fallback={
        <div
          style={{
            fontFamily: "DM Sans",
            color: "#123051",
            padding: "16px",
            textAlign: "center",
          }}
        >
          Loading recorder…
        </div>
      }
    >
      <MediaRecorderInner
        mode={mode}
        maxDurationMs={maxDurationMs}
        onComplete={onComplete}
        onUse={onUse}
        autoStage={autoStage}
      />
    </Suspense>
  );
}
